import {
  flagPublishHackathonRequest,
  IAward,
  IHackathonResponse,
  sendReviewHackathonRequest,
} from "api/hackathon";
import { routesEnum } from "pages/Routes";
import { useMutation, useQueryClient } from "react-query";
import { AxiosError } from "axios";
import { message } from "antd";

import { useHackathonContract, useTokenContract } from "./web3Hooks";
import { CONTRACT_ADDRESSES, NATIVE_TOKEN_ADDRESS } from "utils/walletUtils";
import { useState } from "react";
import { toFixed } from "utils/currency";
import { getHackathonDomain } from "utils/domain";
import { useAppDispatch } from "hooks";
import { toggleWalletModal } from "store/ducks/wallet-modal/slice";
import { formatEther, parseEther } from "@ethersproject/units";
import bigDecimal from "js-big-decimal";
import { CHAIN_BY_NETWORK_KEYS } from "utils/constant";
import { requestSwitchNetwork } from "utils/web3/switchNetwork";
import { handleEthersError } from "utils/web3/handleEthersError";

export const usePublishHackathon = (prizeChainId: number) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const { contract, library, account, chainId, connector } =
    useHackathonContract(CONTRACT_ADDRESSES[prizeChainId]);
  const { getTokenContract } = useTokenContract(
    library as any,
    account as string
  );
  const { mutate: flagPublishHKT } = useMutation(flagPublishHackathonRequest, {
    onSuccess: (_, variable) => {
      const queryKey = ["hackathon", variable.hackathonId];
      const newData: any = queryClient.getQueryData(queryKey);
      newData.flag_publish = variable.flag_publish;
      queryClient.setQueriesData(queryKey, newData);
    },
  });
  const { mutateAsync: sendToReview, isLoading } = useMutation(
    sendReviewHackathonRequest,
    {
      onError: (err: AxiosError) => {
        message.error(err.response?.data?.message);
      },
    }
  );

  const publishCryptoHackathon = async (
    hackathonId: string,
    hackathon: IHackathonResponse
  ) => {
    // If not connect => ask user to connect
    if (!account || !contract) {
      dispatch(toggleWalletModal());
      return;
    }

    if (hackathon?.prizes?.network && prizeChainId !== chainId) {
      return requestSwitchNetwork(
        CHAIN_BY_NETWORK_KEYS[hackathon.prizes.network],
        connector
      );
    }

    setLoading(true);

    const awards: IAward[] = hackathon.prizes.awards.filter(
      (award) =>
        award.prizeCurrency === "nativeToken" || award.prizeCurrency === "token"
    );

    const prizes = awards.reduce(
      (pre: any, cur) => {
        const tokenAddress =
          cur.prizeCurrency === "nativeToken"
            ? NATIVE_TOKEN_ADDRESS
            : cur.token_address;
        return {
          prizeIds: [...pre.prizeIds, cur.name_alias],
          prizeValues: [...pre.prizeValues, parseEther(`${cur.value}`)],
          tokenAddress: [...pre.tokenAddress, tokenAddress],
          prizeCounts: [...pre.prizeCounts, cur.count],
          total: new bigDecimal(
            cur.prizeCurrency === "nativeToken" ? cur.value : 0
          )
            .multiply(new bigDecimal(cur.count))
            .add(pre.total),
        };
      },
      {
        prizeIds: [],
        tokenAddress: [],
        prizeValues: [],
        prizeCounts: [],
        total: new bigDecimal(0),
      }
    );
    try {
      // Approve token before create hackathon
      const approvedAllToken = await approveToken(awards);
      if (!approvedAllToken) {
        setLoading(false);
        return;
      }

      const txParams: any = [
        hackathon?._id,
        hackathon?.essentials?.url,
        prizes.prizeIds,
        prizes.tokenAddress,
        prizes.prizeValues,
        prizes.prizeCounts,
      ];

      const totalPrize = parseEther(
        toFixed(prizes.total.getValue()).toString()
      );

      console.log(totalPrize, txParams, CONTRACT_ADDRESSES[prizeChainId]);

      const tx = await contract.createHackathon(
        {
          arrId: prizes.prizeIds,
          arrNumberOfReceiver: prizes.prizeCounts,
          arrToken: prizes.tokenAddress,
          arrValue: prizes.prizeValues,
          hackathonId: hackathon?._id,
          url: hackathon?.essentials?.url,
        },
        { from: account, value: totalPrize }
      );

      await sendToReview({ hackathonId, transactionId: tx.hash });
      await queryClient.invalidateQueries(["hackathon", hackathonId]);
      await tx.wait(1);
      setLoading(false);
      await queryClient.invalidateQueries(["hackathon", hackathonId]);
      const redirectUrl = `${getHackathonDomain(hackathon.essentials.url)}${
        routesEnum.hackathon
      }/overview/${hackathon._id}`;
      window.location.assign(redirectUrl);
    } catch (err: any) {
      handleEthersError(err);
      setLoading(false);
    }
  };

  const approveToken = async (awards: IAward[]) => {
    if (!account) return;
    try {
      const tokenAwards = awards
        .filter((award) => award.prizeCurrency === "token")
        .reduce((pre: IAward[], cur) => {
          const groupIndex = pre.findIndex(
            (x) => x.token_address === cur.token_address
          );
          if (groupIndex !== -1) {
            const assignPre = [...pre];
            const preCount = assignPre[groupIndex].count;
            const preAmount = assignPre[groupIndex].value;

            const newValue = new bigDecimal(preAmount)
              .multiply(new bigDecimal(preCount))
              .add(
                new bigDecimal(cur.count).multiply(new bigDecimal(cur.value))
              )
              .getValue();
            assignPre[groupIndex].count = 1;
            assignPre[groupIndex].value = Number(newValue);

            return assignPre;
          }
          return [...pre, cur];
        }, []);

      for (let i = 0; i < tokenAwards.length; i++) {
        const tokenContract = await getTokenContract(
          tokenAwards[i].token_address as string
        );

        const totalApproval = new bigDecimal(tokenAwards[i].value)
          .multiply(new bigDecimal(tokenAwards[i].count))
          .getValue();

        const approveAmount = await tokenContract.allowance(
          account,
          CONTRACT_ADDRESSES[prizeChainId]
        );

        const ethApproval = new bigDecimal(
          formatEther(approveAmount)
        ).getValue();

        // Not approve enough amount => Approve more
        if (
          Number(ethApproval) < Number(totalApproval) ||
          Number(ethApproval) === 0
        ) {
          // Execute transaction
          const tx = await tokenContract.approve(
            CONTRACT_ADDRESSES[prizeChainId],
            parseEther(totalApproval),
            { from: account }
          );
          await tx.wait();
        }
      }

      return true;
    } catch (err: any) {
      handleEthersError(err);
      setLoading(false);
    }
  };

  const publishNonCrytoHackathon = async (
    id: string,
    hackathon: IHackathonResponse
  ) => {
    await sendToReview({ hackathonId: id });
    queryClient.invalidateQueries(["hackathon", id]);

    const redirectUrl = `${getHackathonDomain(hackathon.essentials.url)}${
      routesEnum.hackathon
    }/overview/${hackathon._id}`;
    window.location.assign(redirectUrl);
  };

  const mutate = async (id: string, hackathon: IHackathonResponse) => {
    try {
      const isHaveCrytoAward = hackathon.prizes.awards.findIndex(
        (award) =>
          award.prizeCurrency === "nativeToken" ||
          award.prizeCurrency === "token"
      );

      if (isHaveCrytoAward !== -1 && hackathon.status !== "rejected") {
        flagPublishHKT({ hackathonId: id, flag_publish: true });
        // Crypto case
        await publishCryptoHackathon(id, hackathon);
      } else {
        // Non-crypto case
        return await publishNonCrytoHackathon(id, hackathon);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message);
    }
  };

  return {
    mutate,
    needConnect: !library,
    isLoading: isLoading || loading,
    chainId,
  };
};
